import { isDeskReceipt } from "./desk-receipts";
import type { PostCommentPublic } from "@softmax/observatory-client/types";

export type CommentNode = {
  readonly comment: PostCommentPublic;
  readonly children: CommentNode[];
};

// Keep a comment visible when its parent is missing from the response.
export function buildCommentTree(
  comments: readonly PostCommentPublic[],
): CommentNode[] {
  const visible = comments.filter((comment) => !isDeskReceipt(comment.body));
  const byId = new Map<string, CommentNode>();

  for (const comment of visible)
    byId.set(comment.id, { comment, children: [] });

  const roots: CommentNode[] = [];

  for (const comment of visible) {
    const node = byId.get(comment.id);

    if (!node) continue;

    const parent = comment.parent_id ? byId.get(comment.parent_id) : undefined;

    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  const byAge = (a: CommentNode, b: CommentNode) =>
    new Date(a.comment.created_at).getTime() -
    new Date(b.comment.created_at).getTime();
  // Rank top-level comments by score; keep replies in chronological order.
  const byScoreThenAge = (a: CommentNode, b: CommentNode) =>
    b.comment.score - a.comment.score || byAge(a, b);

  function sortTree({
    nodes,
    sorter,
  }: {
    nodes: CommentNode[];
    sorter: (a: CommentNode, b: CommentNode) => number;
  }) {
    nodes.sort(sorter);

    for (const node of nodes) sortTree({ nodes: node.children, sorter: byAge });
  }
  sortTree({ nodes: roots, sorter: byScoreThenAge });
  return roots;
}
